import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { NavigationTracker } from "./components/NavigationTracker";
import ScrollToTop from "./components/ScrollToTop";
import { CookieConsentProvider } from "./hooks/useCookieConsent";
import CookieBanner from "./components/CookieBanner";
import CookiePreferencesModal from "./components/CookiePreferencesModal";
import { PageViewTracker } from "./components/PageViewTracker";

// Eager load - Page d'accueil (critique pour LCP)
import Index from "./pages/Index";

// Lazy load - Pages secondaires
const ChoisirEureka = lazy(() => import("./pages/ChoisirEureka"));
const ChoisirEurekaEntreprise = lazy(() => import("./pages/ChoisirEurekaEntreprise"));
const CVBuilder = lazy(() => import("./pages/CVBuilder"));
const Contact = lazy(() => import("./pages/Contact"));
const PostulerSansCV = lazy(() => import("./pages/PostulerSansCV"));
const ParcoursImpulsion = lazy(() => import("./pages/ParcoursImpulsion"));
const ParcoursHandicap = lazy(() => import("./pages/ParcoursHandicap"));
const ParcoursCarriereProgression = lazy(() => import("./pages/ParcoursCarriereProgression"));
const SolutionInclusion = lazy(() => import("./pages/SolutionInclusion"));
const SolutionHandicap = lazy(() => import("./pages/SolutionHandicap"));
const SolutionExpert = lazy(() => import("./pages/SolutionExpert"));
const ConseilStrategieRH = lazy(() => import("./pages/ConseilStrategieRH"));
const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));
const PolitiqueConfidentialite = lazy(() => import("./pages/PolitiqueConfidentialite"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Fallback minimal pour le chargement
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <CookieConsentProvider>
          <ScrollToTop />
          <NavigationTracker />
          <PageViewTracker />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/choisir-eureka" element={<ChoisirEureka />} />
              <Route path="/choisir-eureka-entreprise" element={<ChoisirEurekaEntreprise />} />
              <Route path="/parcours-impulsion" element={<ParcoursImpulsion />} />
              <Route path="/parcours-handicap" element={<ParcoursHandicap />} />
              <Route path="/parcours-carriere-progression" element={<ParcoursCarriereProgression />} />
              <Route path="/solution-inclusion" element={<SolutionInclusion />} />
              <Route path="/solution-handicap" element={<SolutionHandicap />} />
              <Route path="/solution-expert" element={<SolutionExpert />} />
              <Route path="/conseil-strategie-rh" element={<ConseilStrategieRH />} />
              <Route path="/mentions-legales" element={<MentionsLegales />} />
              <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
              <Route path="/cookies" element={<Cookies />} />
              <Route path="/cv-builder" element={<CVBuilder />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/postuler-sans-cv" element={<PostulerSansCV />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieBanner />
          <CookiePreferencesModal />
        </CookieConsentProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
